import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Alert, Button, Card, EmptyState, ErrorState, IconAction, Input, Select, Tabs, TextSkeleton, cn,
} from '@dru/ui';
import { Archive, BellRing, Check, CheckCheck, Inbox, Search, X } from 'lucide-react';
import { api } from '../lib/api';
import { errorMessage, useAuth } from '../lib/auth';
import { PageHeader } from '../layouts/ConsoleShell';
import {
  CATEGORY, NOTIFICATIONS_CHANGED, categoryOf, notificationTarget, type NotificationCategory,
} from '../lib/notificationCategories';
import { permittedLink } from './NotificationCenter';

interface Item {
  id: string;
  eventType: string;
  category: NotificationCategory;
  subject: string | null;
  body: string;
  linkPath: string | null;
  entityType: string | null;
  entityId: string | null;
  createdAt: string;
  readAt: string | null;
  archivedAt: string | null;
}
interface Feed { items: Item[]; next: string | null }

type View = 'UNREAD' | 'READ' | 'ARCHIVED';
type Range = 'any' | 'today' | '7' | '30';

const PAGE = 30;
/** The feed has no "read" state; a page of it is filtered, so a few pages may be read to fill one. */
const MAX_PAGES_PER_FILL = 5;

/** The filter chips each console offers: what its people are told about. */
const CHIPS: Record<string, NotificationCategory[]> = {
  '/hr': ['checkup', 'appointment', 'certificate', 'compliance', 'camp', 'billing'],
  '/admin': ['lead', 'company', 'billing', 'system', 'account'],
  '/ops': ['lead', 'company', 'billing', 'system', 'case'],
  '/partner': ['case', 'camp', 'result'],
  '/lab': ['case', 'camp', 'result'],
  '/hospital': ['case', 'camp'],
  '/physician': ['review', 'result'],
};

/**
 * Events that ask somebody to do something, from the API's catalogue. They
 * lead the Unread tab and carry a marker; everything else is information.
 * `danger` ones are about a person's health or a lapsed obligation.
 */
const ATTENTION: Record<string, { tone: 'danger' | 'warning'; action: string }> = {
  'critical.alert': { tone: 'danger', action: 'Review result' },
  'appointment.missed': { tone: 'danger', action: 'Rebook' },
  'compliance.overdue': { tone: 'danger', action: 'See who is overdue' },
  'certificate.revoked': { tone: 'danger', action: 'View certificate' },
  'certificate.expiring': { tone: 'warning', action: 'Plan renewal' },
  'consent.pending': { tone: 'warning', action: 'Open case' },
  'campaign.scheduled': { tone: 'warning', action: 'View campaign' },
  'review.correction-requested': { tone: 'warning', action: 'Open review' },
  'payment.reviewed': { tone: 'warning', action: 'View payment' },
  'system.service-down': { tone: 'danger', action: 'Open' },
};

/** The verb on the link of an ordinary notification, by what it is about. */
const OPEN_LABEL: Partial<Record<NotificationCategory, string>> = {
  appointment: 'View appointment',
  certificate: 'View certificate',
  billing: 'View invoice',
  camp: 'View campaign',
  checkup: 'Open case',
  case: 'Open case',
  review: 'Open review',
  result: 'View result',
  lead: 'Open lead',
  company: 'Open company',
};

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
function dayLabel(d: Date): string {
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  if (dayKey(d) === dayKey(today)) return 'Today';
  if (dayKey(d) === dayKey(yesterday)) return 'Yesterday';
  return d.toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short',
    ...(d.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}),
  });
}
const timeOf = (d: Date) => d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });

function fromDate(range: Range): string | null {
  if (range === 'any') return null;
  const d = new Date();
  if (range === 'today') d.setHours(0, 0, 0, 0);
  else d.setDate(d.getDate() - Number(range));
  return d.toISOString();
}

const VIEW_KEYS: View[] = ['UNREAD', 'READ', 'ARCHIVED'];

/**
 * Notifications, for every console. Three tabs, because they are three
 * different things: Unread is new, Read is seen but not dealt with, Archived
 * is dealt with. Nothing is ever deleted — each row is the record of what was
 * sent, so archiving only moves it out of the way.
 *
 * The unread count is the API's, never a count of the rows on screen. Every
 * read comes from `/notifications/*`, which takes the recipient from the
 * session, so one person's inbox cannot be asked for by another.
 */
export function NotificationsPage() {
  const { pathname } = useLocation();
  const { permissions } = useAuth();
  const prefix = `/${pathname.split('/')[1] ?? ''}`;
  const chips = CHIPS[prefix] ?? [];

  const [view, setView] = useState<View>('UNREAD');
  const [range, setRange] = useState<Range>('any');
  const [cats, setCats] = useState<NotificationCategory[]>([]);
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [items, setItems] = useState<Item[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actError, setActError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [unread, setUnread] = useState(0);
  const req = useRef(0);
  /** Set while this page announces its own change, so it does not reload for it. */
  const own = useRef(false);

  // Search as you type, without a request per keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => setTerm(q.trim()), 300);
    return () => window.clearTimeout(t);
  }, [q]);

  const url = useCallback((before?: string | null) => {
    const p = new URLSearchParams({ limit: String(PAGE) });
    // The feed knows UNREAD and ARCHIVED; READ is the not-archived feed
    // with the unread ones left out below.
    if (view !== 'READ') p.set('state', view);
    if (cats.length) p.set('category', cats.join(','));
    if (term) p.set('q', term);
    const from = fromDate(range);
    if (from) p.set('from', from);
    if (before) p.set('before', before);
    return `/notifications/feed?${p}`;
  }, [view, cats, term, range]);

  /** One screenful from `before` on: for Read, up to a few pages until it is full. */
  const fill = useCallback(async (before: string | null) => {
    const out: Item[] = [];
    let cursor = before;
    for (let i = 0; i < MAX_PAGES_PER_FILL; i += 1) {
      const feed = await api.get<Feed>(url(cursor));
      out.push(...(view === 'READ' ? feed.items.filter((n) => n.readAt) : feed.items));
      cursor = feed.next;
      if (view !== 'READ' || !cursor || out.length >= PAGE) break;
    }
    return { items: out, next: cursor };
  }, [url, view]);

  const load = useCallback(async () => {
    const mine = ++req.current;
    setLoading(true); setError(null);
    try {
      const [page, count] = await Promise.all([
        fill(null),
        api.get<{ unread: number }>('/notifications/inbox/unread-count'),
      ]);
      if (mine !== req.current) return;
      setItems(page.items); setNext(page.next); setUnread(count.unread);
    } catch (e) {
      if (mine === req.current) setError(errorMessage(e));
    } finally {
      if (mine === req.current) setLoading(false);
    }
  }, [fill]);

  useEffect(() => { void load(); }, [load]);

  // The bell announces arrivals and the reads it makes; the page follows it.
  useEffect(() => {
    const again = () => { if (own.current) { own.current = false; return; } void load(); };
    window.addEventListener(NOTIFICATIONS_CHANGED, again);
    return () => window.removeEventListener(NOTIFICATIONS_CHANGED, again);
  }, [load]);

  const loadMore = async () => {
    if (!next) return;
    setMore(true); setActError(null);
    try {
      const page = await fill(next);
      setItems((s) => [...s, ...page.items]); setNext(page.next);
    } catch (e) { setActError(errorMessage(e)); } finally { setMore(false); }
  };

  /**
   * Runs an action, takes the row out of a tab it no longer belongs to, and
   * tells the bell. The page skips reloading for its own announcement (the
   * listener runs synchronously inside dispatchEvent), so the list does not flash.
   */
  const act = async (key: string, fn: () => Promise<unknown>, patch: (i: Item) => Item | null, countDelta: (n: number) => number) => {
    setBusy(key); setActError(null);
    try {
      await fn();
      setItems((s) => s.map(patch).filter((i): i is Item => i !== null));
      setUnread(countDelta);
      own.current = true;
      window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED));
      own.current = false;
    } catch (e) { setActError(errorMessage(e)); } finally { setBusy(null); }
  };
  const markRead = (n: Item) => act(
    `r-${n.id}`, () => api.post(`/notifications/${n.id}/read`, {}),
    (i) => (i.id === n.id ? (view === 'UNREAD' ? null : { ...i, readAt: new Date().toISOString() }) : i),
    (c) => Math.max(0, c - 1),
  );
  const archive = (n: Item) => act(
    `a-${n.id}`, () => api.post(`/notifications/${n.id}/archive`, {}),
    (i) => (i.id === n.id ? null : i),
    // Archiving an unread one also takes it out of the count.
    (c) => (n.readAt ? c : Math.max(0, c - 1)),
  );
  const readAll = () => act(
    'all', () => api.post('/notifications/inbox/read-all', {}),
    (i) => (view === 'UNREAD' ? null : { ...i, readAt: i.readAt ?? new Date().toISOString() }),
    () => 0,
  );
  const openRow = (n: Item) => {
    if (n.readAt) return;
    void api.post(`/notifications/${n.id}/read`, {})
      .then(() => window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED)))
      .catch(() => undefined);
  };

  // On Unread, what asks for action leads; the rest follows by day.
  const attention = useMemo(
    () => (view === 'UNREAD' ? items.filter((n) => ATTENTION[n.eventType]) : []),
    [items, view],
  );
  const groups = useMemo(() => {
    const rest = view === 'UNREAD' ? items.filter((n) => !ATTENTION[n.eventType]) : items;
    const out: Array<{ label: string; items: Item[] }> = [];
    for (const n of rest) {
      const label = dayLabel(new Date(n.createdAt));
      const last = out[out.length - 1];
      if (last?.label === label) last.items.push(n); else out.push({ label, items: [n] });
    }
    return out;
  }, [items, view]);

  const narrowed = range !== 'any' || cats.length > 0 || term !== '';
  const clear = () => { setRange('any'); setCats([]); setQ(''); };
  const toggle = (c: NotificationCategory) =>
    setCats((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));

  const row = (n: Item, opts: { showDay?: boolean } = {}) => {
    const meta = categoryOf(n.category);
    const at = new Date(n.createdAt);
    const to = permittedLink(notificationTarget(n, prefix, permissions), permissions);
    const isUnread = !n.readAt;
    const flag = ATTENTION[n.eventType];
    const title = n.subject ?? meta.label;
    return (
      <li
        key={n.id}
        className={cn('relative flex gap-3 px-3 py-3 sm:px-4', isUnread && 'bg-primary-50/40')}
      >
        {/* A bar, not a border: the list's dividers recolour every border of a row. */}
        {flag && (
          <span aria-hidden className={cn('absolute inset-y-0 left-0 w-[3px]', flag.tone === 'danger' ? 'bg-danger-600' : 'bg-warning-600')} />
        )}
        <span aria-hidden className={cn('mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full', meta.tone)}>
          <meta.icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <p className={cn('min-w-0 text-body', isUnread ? 'font-semibold text-neutral-900' : 'font-medium text-neutral-800')}>
              {isUnread && <span className="sr-only">Unread: </span>}
              {title}
            </p>
            {flag && (
              <span className={cn(
                'rounded-badge px-1.5 text-micro font-semibold uppercase leading-5 tracking-wide',
                flag.tone === 'danger' ? 'bg-danger-100 text-danger-600' : 'bg-warning-100 text-warning-600',
              )}>
                Action needed
              </span>
            )}
          </div>
          <p className="mt-0.5 line-clamp-3 break-words text-small text-neutral-600">{n.body}</p>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
            {to && (
              <Link
                to={to}
                onClick={() => openRow(n)}
                className={cn(
                  'inline-flex h-8 items-center rounded-btn px-3 text-small font-semibold transition-colors max-md:h-10',
                  flag ? 'bg-primary-600 text-white hover:bg-primary-700' : 'border border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-50',
                )}
              >
                {flag?.action ?? OPEN_LABEL[n.category] ?? 'Open'}
              </Link>
            )}
            <span className="text-caption text-neutral-500">
              <time dateTime={n.createdAt} title={at.toLocaleString('en-IN')}>
                {opts.showDay ? `${dayLabel(at)}, ${timeOf(at)}` : timeOf(at)}
              </time>
              <span aria-hidden> · </span>
              {meta.label}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 items-start gap-0.5">
          {isUnread && (
            <IconAction label={`Mark as read: ${title}`} icon={Check} loading={busy === `r-${n.id}`} onClick={() => void markRead(n)} />
          )}
          {!n.archivedAt && (
            <IconAction label={`Archive: ${title}`} icon={Archive} loading={busy === `a-${n.id}`} onClick={() => void archive(n)} />
          )}
        </div>
      </li>
    );
  };

  const empty = narrowed
    ? { title: 'Nothing matches', hint: 'Try another topic or time, or clear the filters.' }
    : view === 'UNREAD'
      ? { title: 'You are all caught up', hint: 'New alerts appear here and on the bell as they happen.' }
      : view === 'READ'
        ? { title: 'Nothing read yet', hint: 'Notifications you have seen stay here until you archive them.' }
        : { title: 'Nothing archived', hint: 'Archive what you have dealt with. It is kept here, never deleted.' };

  return (
    <div className="max-w-4xl space-y-4">
      <PageHeader
        className="mb-0"
        title="Notifications"
        hint="Unread is new. Read is seen. Archive what you have dealt with — archived notifications are kept, never deleted."
        actions={unread > 0 && view === 'UNREAD' && (
          <Button variant="secondary" icon={<CheckCheck className="h-4 w-4" />} loading={busy === 'all'} onClick={() => void readAll()}>
            Mark all read
          </Button>
        )}
      />

      <Tabs
        tabs={VIEW_KEYS.map((v) => ({
          key: v,
          label: v === 'UNREAD' ? 'Unread' : v === 'READ' ? 'Read' : 'Archived',
          hint: v === 'UNREAD' && unread > 0 ? (unread > 99 ? '99+' : unread) : undefined,
        }))}
        active={view}
        onChange={(k) => setView(k as View)}
      />

      <div className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" aria-hidden />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notifications" aria-label="Search notifications" className="h-10 pl-9" />
          </div>
          <Select value={range} onChange={(e) => setRange(e.target.value as Range)} aria-label="When" className="h-10 w-full sm:w-40">
            <option value="any">Any time</option>
            <option value="today">Today</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
          </Select>
        </div>
        {chips.length > 0 && (
          // One scrolling row on a phone, not three rows of chips above the list.
          <div className="flex flex-wrap items-center gap-2 max-md:-mx-4 max-md:flex-nowrap max-md:overflow-x-auto max-md:px-4 max-md:pb-1" role="group" aria-label="Filter by topic">
            {chips.map((c) => {
              const meta = CATEGORY[c];
              const on = cats.includes(c);
              return (
                <button
                  key={c} type="button" aria-pressed={on} onClick={() => toggle(c)}
                  className={cn(
                    'inline-flex min-h-8 shrink-0 items-center gap-1.5 rounded-full border px-3 text-caption font-medium transition-colors max-md:min-h-10',
                    on ? 'border-primary-300 bg-primary-50 text-primary-800' : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50',
                  )}
                >
                  <meta.icon className="h-3.5 w-3.5" aria-hidden />
                  {meta.label}
                </button>
              );
            })}
            {narrowed && (
              <button type="button" onClick={clear} className="inline-flex min-h-8 shrink-0 items-center gap-1 px-2 text-caption font-medium text-neutral-500 hover:text-neutral-800 max-md:min-h-10">
                <X className="h-3.5 w-3.5" aria-hidden /> Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {actError && <Alert tone="danger">{actError}</Alert>}

      {error ? (
        <ErrorState title="Could not load notifications" detail={error} onRetry={() => void load()} />
      ) : loading && items.length === 0 ? (
        <Card className="p-5"><TextSkeleton lines={6} /></Card>
      ) : items.length === 0 ? (
        <EmptyState
          icon={view === 'ARCHIVED' ? <Archive className="h-6 w-6" /> : <Inbox className="h-6 w-6" />}
          title={empty.title}
          hint={empty.hint}
          action={narrowed ? <Button variant="secondary" onClick={clear}>Clear filters</Button>
            : view === 'UNREAD' ? <Button variant="secondary" onClick={() => setView('READ')}>See read notifications</Button>
              : undefined}
        />
      ) : (
        <div className="space-y-4">
          {attention.length > 0 && (
            <section aria-labelledby="attention-h">
              <h2 id="attention-h" className="mb-2 flex items-center gap-1.5 text-small font-semibold text-neutral-900">
                <BellRing className="h-4 w-4 text-danger-600" aria-hidden />
                Needs your attention
                <span className="font-normal text-neutral-500">({attention.length})</span>
              </h2>
              <Card className="overflow-hidden">
                <ul className="divide-y divide-neutral-100">{attention.map((n) => row(n, { showDay: true }))}</ul>
              </Card>
            </section>
          )}

          {groups.map((g) => (
            <section key={g.label} aria-label={g.label}>
              <h2 className="mb-2 text-micro font-semibold uppercase tracking-wide text-neutral-500">{g.label}</h2>
              <Card className="overflow-hidden">
                <ul className="divide-y divide-neutral-100">{g.items.map((n) => row(n))}</ul>
              </Card>
            </section>
          ))}

          {next && (
            <div className="flex justify-center">
              <Button variant="secondary" loading={more} onClick={() => void loadMore()}>Show older</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
